import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, FormControl } from '@angular/forms';
import { MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { Agreement } from 'app/entities/transaction-profile/agreement.model';
import { Document } from 'app/shared/models/document.model';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { EntityTransactionProfileService } from 'app/entities/transaction-profile/entity-transaction-profile.service';
import { EntityDocumentsService } from 'app/entities/documents/entity-documents.service';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';
import { ConstantsService } from 'app/services/constants-service';
import { CookieService } from 'app/services/cookie.service';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { User } from 'app/shared/models/user.model';
import { Observable } from 'rxjs';
import { ObservableRequestService } from 'app/services/observable-request.service';

interface UploadedDocument {
    file: File;
    documentId?: number;
    documentType?: number;
    documentTypeName?: string;
    tags: Array<{ id: number; name: string }>;
    isAnalyzing: boolean;
    analysisError?: string;
    metadata: {
        documentDescription?: string;
        startDate?: string;
        endDate?: string;
        passportNumber?: string;
        validFrom?: string;
        validTo?: string;
        issuedBy?: number;
        verifiedBy?: number;
        certifiedBy?: string;
    };
    formGroup: FormGroup;
}

@Component({
    selector: 'agreement-documents-dialog',
    templateUrl: './agreement-documents-dialog.component.html',
    styleUrls: ['./agreement-documents-dialog.component.css']
})
export class AgreementDocumentsDialogComponent implements OnInit {
    agreement: Agreement;
    entityIdDetails: EntityIdDetails;
    documents: Document[] = [];
    documentsLoaded = false;
    zeroAggrement = false;
    
    // Upload properties
    uploadedDocuments: UploadedDocument[] = [];
    documentTypes: ApplicationValue[] = [];
    documentLabels = [];
    countries = [];
    users: User[];
    loginUser: string;
    isUploading = false;
    showUploadSection = false;
    dragOver = false;
    
    // Tag search state per document
    tagSearchTexts: { [index: number]: string } = {};
    showTagDropdowns: { [index: number]: boolean } = {};
    
    // Tag search state for edit mode
    tagSearchTextsEdit: { [documentId: number]: string } = {};
    showTagDropdownsEdit: { [documentId: number]: boolean } = {};
    
    // Expanded rows for metadata display
    expandedRows: { [documentId: number]: boolean } = {};
    
    // Pagination for custom table
    currentPage: number = 0;
    rowsPerPage: number = 10;
    totalPages: number = 0;
    
    // Editing state
    editingDocumentId: number | null = null;
    editFormData: { [documentId: number]: any } = {};
    
    startEditing(document: Document) {
        this.editingDocumentId = document.id;
        
        // Find "Other" type if document type is not set
        let documentType = document.type;
        if (!documentType) {
            const otherType = this.documentTypes.find(dt => 
                dt.name && dt.name.toLowerCase() === 'other'
            ) || this.documentTypes.find(dt => 
                dt.name && dt.name.toLowerCase().includes('other')
            );
            documentType = otherType ? otherType.id : (this.documentTypes.length > 0 ? this.documentTypes[0].id : null);
        }
        
        // Initialize edit form data
        this.editFormData[document.id] = {
            documentType: documentType,
            selectedLabels: this.getDocumentTags(document).map(tag => ({ id: tag.id, name: tag.name })),
            documentDescription: this.getMetadataValue(document, 'otherDetails'),
            startDate: this.getMetadataValue(document, 'startDate'),
            endDate: this.getMetadataValue(document, 'endDate'),
            passportNumber: this.getMetadataValue(document, 'passportNumber'),
            validFrom: this.getMetadataValue(document, 'validFrom'),
            validTo: this.getMetadataValue(document, 'validTo'),
            issuedBy: this.getMetadataValue(document, 'issuedBy') ? parseInt(this.getMetadataValue(document, 'issuedBy'), 10) : null,
            verifiedBy: this.getMetadataValue(document, 'verifiedBy') ? parseInt(this.getMetadataValue(document, 'verifiedBy'), 10) : null,
            certifiedBy: this.getMetadataValue(document, 'certifiedBy')
        };
    }
    
    cancelEditing() {
        this.editingDocumentId = null;
    }
    
    saveDocument(document: Document) {
        if (!this.editFormData[document.id]) {
            return;
        }
        
        const formData = this.editFormData[document.id];
        const documentTypeName = this.getSelectedDocumentTypeNameEdit(document.id);
        
        // Create updated document
        const updatedDocument = this.createDocumentForUpdate(document.id, formData, documentTypeName);
        
        this.entityDocumentsService.addDocument([updatedDocument])
            .subscribe(
                response => {
                    this.snackBarService.showStatus(
                        response,
                        MessageService.ADDED_DOCUMENT_SUCCESS,
                        MessageService.DOCUMENT_FAILURE
                    );
                    this.editingDocumentId = null;
                    delete this.editFormData[document.id];
                    this.reloadDocuments();
                },
                err => {
                    this.snackBarService.showStatus(
                        err,
                        '',
                        MessageService.DOCUMENT_FAILURE
                    );
                }
            );
    }
    
    getMetadataValue(document: Document, key: string): string {
        if (!document || !document.allMeta) {
            return '';
        }
        
        if (document.allMeta instanceof Map) {
            return document.allMeta.get(key) || '';
        } else if (typeof document.allMeta === 'object' && document.allMeta !== null) {
            return (document.allMeta as any)[key] || '';
        }
        
        return '';
    }
    
    isEditing(documentId: number): boolean {
        return this.editingDocumentId === documentId;
    }
    
    getSelectedDocumentTypeNameEdit(documentId: number): string {
        if (!this.editFormData[documentId] || !this.editFormData[documentId].documentType) {
            return '';
        }
        const selectedTypeId = this.editFormData[documentId].documentType;
        const selectedDocument = this.documentTypes.find(doc => doc.id === selectedTypeId);
        return selectedDocument ? selectedDocument.name : '';
    }
    
    // Edit mode tag methods
    getFilteredTagsEdit(documentId: number): any[] {
        const searchText = (this.tagSearchTextsEdit[documentId] || '').toLowerCase();
        const editData = this.editFormData[documentId];
        const selectedTagIds = (editData && editData.selectedLabels ? editData.selectedLabels : []).map((tag: any) => tag.id);
        
        return this.documentLabels.filter(label => {
            const matchesSearch = !searchText || label.name.toLowerCase().includes(searchText);
            const notSelected = !selectedTagIds.includes(label.value.id);
            return matchesSearch && notSelected;
        });
    }
    
    onTagSearchEdit(documentId: number, searchText: string) {
        this.tagSearchTextsEdit[documentId] = searchText;
        this.showTagDropdownsEdit[documentId] = true;
        this.cdr.detectChanges();
    }
    
    onTagInputFocusEdit(documentId: number) {
        this.showTagDropdownsEdit[documentId] = true;
        this.cdr.detectChanges();
    }
    
    onTagInputBlurEdit(documentId: number) {
        setTimeout(() => {
            this.showTagDropdownsEdit[documentId] = false;
            this.cdr.detectChanges();
        }, 200);
    }
    
    onTagContainerClickEdit(documentId: number, event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target.classList.contains('tag-chips-container')) {
            const input = target.querySelector('.tag-search-input') as HTMLInputElement;
            if (input) {
                input.focus();
            }
        }
    }
    
    addTagEdit(documentId: number, tag: any) {
        if (!this.editFormData[documentId]) {
            return;
        }
        
        const currentTags = this.editFormData[documentId].selectedLabels || [];
        
        if (!currentTags.find((t: any) => t.id === tag.value.id)) {
            currentTags.push(tag.value);
            this.editFormData[documentId].selectedLabels = currentTags;
        }
        
        this.tagSearchTextsEdit[documentId] = '';
        this.showTagDropdownsEdit[documentId] = false;
        this.cdr.detectChanges();
    }
    
    removeTagEdit(documentId: number, tagId: number) {
        if (!this.editFormData[documentId]) {
            return;
        }
        
        const currentTags = this.editFormData[documentId].selectedLabels || [];
        this.editFormData[documentId].selectedLabels = currentTags.filter((t: any) => t.id !== tagId);
        this.cdr.detectChanges();
    }

    constructor(
        public dialog: MdDialogRef<AgreementDocumentsDialogComponent>,
        @Inject(MD_DIALOG_DATA) public data: { agreement: Agreement, entityIdDetails: EntityIdDetails },
        private transactionProfileService: EntityTransactionProfileService,
        private snackBarService: SnackBarService,
        private trackingService: TrackingService,
        private formBuilder: FormBuilder,
        private entityDocumentsService: EntityDocumentsService,
        private entityDetailsService: EntityDetailsService,
        private warmUpService: WarmUpService,
        private cookieService: CookieService,
        private cdr: ChangeDetectorRef,
        private requestService: ObservableRequestService
    ) {
        if (data) {
            this.agreement = data.agreement;
            this.entityIdDetails = data.entityIdDetails;
        }
    }

    ngOnInit() {
        this.loadDocuments();
        this.loadDocumentTypes();
        this.loadDocumentLabels();
        this.loadCountries();
        this.loginUser = this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME);
        const usersList = this.warmUpService.getUsersList();
        // Convert to array if it's a Map or has values() method
        if (usersList instanceof Map) {
            this.users = Array.from(usersList.values());
        } else if (usersList && typeof usersList === 'object' && typeof usersList.values === 'function') {
            this.users = Array.from(usersList.values());
        } else if (Array.isArray(usersList)) {
            this.users = usersList;
        } else {
            this.users = [];
        }
    }

    loadDocumentTypes() {
        const documentTypeValues = this.warmUpService.getApplicationValue(ApplicationTypeConstants.DOCUMENT_TYPE);
        if (documentTypeValues instanceof Observable) {
            documentTypeValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    // Filter out any document types with null/undefined ids to ensure all are selectable
                    this.documentTypes = (response.values as ApplicationValue[] || []).filter(dt => dt && dt.id != null && dt.id !== undefined);
                    this.cdr.detectChanges();
                }
            );
        } else {
            // Filter out any document types with null/undefined ids to ensure all are selectable
            this.documentTypes = (documentTypeValues as ApplicationValue[] || []).filter(dt => dt && dt.id != null && dt.id !== undefined);
        }
    }

    loadDocumentLabels() {
        this.entityDocumentsService.getDocumentLabels().subscribe(
            response => {
                let values = response.arrResult['values'];
                // Convert to array if it's a Map or has values() method
                if (values instanceof Map) {
                    values = Array.from(values.values());
                } else if (values && typeof values === 'object' && typeof values.values === 'function') {
                    values = Array.from(values.values());
                } else if (!Array.isArray(values)) {
                    values = [];
                }
                
                for (const labelObj of values) {
                    this.documentLabels.push({ 
                        label: labelObj.name, 
                        value: { id: labelObj.id, name: labelObj.name },
                        name: labelObj.name
                    });
                }
            }
        );
    }

    loadCountries() {
        const setCountries = (values) => { 
            // Convert to array if it's a Map or has values() method
            if (values instanceof Map) {
                this.countries = Array.from(values.values());
            } else if (values && typeof values === 'object' && typeof values.values === 'function') {
                this.countries = Array.from(values.values());
            } else if (Array.isArray(values)) {
                this.countries = values;
            } else {
                this.countries = [];
            }
        };
        this.warmUpService.getApplicationValueList(ApplicationTypeConstants.COUNTRY_TYPE, setCountries);
    }

    toggleUploadSection() {
        this.showUploadSection = !this.showUploadSection;
        if (!this.showUploadSection) {
            this.uploadedDocuments = [];
        }
    }

    onFileSelected(event: any) {
        const files = Array.from(event.target.files) as File[];
        this.uploadFilesDirectly(files);
    }

    uploadFilesDirectly(files: File[]) {
        if (files.length === 0) {
            return;
        }

        this.isUploading = true;
        
        // Create a simple FileList-like object
        const fileList = this.createFileList(files);
        const xhr = this.transactionProfileService.uploadDocumentForAgreement(
            fileList as any,
            this.entityIdDetails.entityId,
            this.agreement.id
        );

        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    let response = false;
                    let documentIds = [];
                    if (xhr.response) {
                        const parsedResponse = JSON.parse(xhr.response);
                        response = parsedResponse.flagResult;
                        documentIds = parsedResponse.arrResult || [];
                        
                        if (documentIds.length > 0) {
                            // Find "Other" document type
                            const otherType = this.documentTypes.find(dt => 
                                dt.name && dt.name.toLowerCase() === 'other'
                            ) || this.documentTypes.find(dt => 
                                dt.name && dt.name.toLowerCase().includes('other')
                            ) || (this.documentTypes.length > 0 ? this.documentTypes[0] : null);
                            
                            // Create documents with default values and add to table
                            const documentsToSave = [];
                            documentIds.forEach((docId, index) => {
                                if (files[index]) {
                                    const document = new Document();
                                    document.id = docId;
                                    document.name = files[index].name;
                                    document.type = otherType ? otherType.id : null;
                                    document.entityId = this.entityIdDetails.entityId;
                                    document.docOwner = this.loginUser;
                                    document.dateOfUpload = new Date();
                                    
                                    // Set description metadata to filename
                                    if (document.allMeta instanceof Map) {
                                        document.allMeta.set('otherDetails', files[index].name);
                                    } else {
                                        document.allMeta = new Map();
                                        document.allMeta.set('otherDetails', files[index].name);
                                    }
                                    
                                    documentsToSave.push(document);
                                }
                            });
                            
                            // Save documents with default metadata first, then extract and update
                            if (documentsToSave.length > 0) {
                                this.entityDocumentsService.addDocument(documentsToSave)
                                    .subscribe(
                                        response => {
                                            // After saving, extract metadata for each document
                                            documentsToSave.forEach((doc, index) => {
                                                this.extractAndUpdateDocumentMetadata(doc.id, files[index].name);
                                            });
                                            
                                            this.snackBarService.showStatus(
                                                response,
                                                MessageService.ADDED_DOCUMENT_SUCCESS,
                                                MessageService.DOCUMENT_FAILURE
                                            );
                                            this.isUploading = false;
                                            this.toggleUploadSection();
                                            this.reloadDocuments();
                                        },
                                        err => {
                                            this.snackBarService.showStatus(
                                                err,
                                                '',
                                                MessageService.DOCUMENT_FAILURE
                                            );
                                            this.isUploading = false;
                                        }
                                    );
                            } else {
                                this.isUploading = false;
                                this.reloadDocuments();
                            }
                        } else if (response) {
                            this.snackBarService.showStatus(
                                parsedResponse,
                                MessageService.ADDED_DOCUMENT_SUCCESS,
                                MessageService.DOCUMENT_FAILURE
                            );
                            this.isUploading = false;
                            this.toggleUploadSection();
                            this.reloadDocuments();
                        } else {
                            this.snackBarService.showStatus(
                                parsedResponse,
                                '',
                                MessageService.DOCUMENT_FAILURE
                            );
                            this.isUploading = false;
                        }
                    }
                } else {
                    this.snackBarService.showStatus(
                        { flagResult: false },
                        '',
                        MessageService.DOCUMENT_FAILURE
                    );
                    this.isUploading = false;
                }
            }
        };
    }

    createDocumentFormGroup(): FormGroup {
        return this.formBuilder.group({
            documentType: ['', Validators.required],
            selectedLabels: [[]],
            documentDescription: [''],
            startDate: [''],
            endDate: [''],
            passportNumber: [''],
            validFrom: [''],
            validTo: [''],
            issuedBy: [''],
            verifiedBy: [''],
            certifiedBy: ['']
        });
    }

    removeUploadedDocument(index: number) {
        this.uploadedDocuments.splice(index, 1);
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = true;
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dragOver = false;
        
        const files = event.dataTransfer && event.dataTransfer.files;
        if (files && files.length > 0) {
            this.uploadFilesDirectly(Array.from(files) as File[]);
        }
    }

    uploadDocuments() {
        if (this.uploadedDocuments.length === 0) {
            return;
        }

        this.isUploading = true;
        
        // First, upload all files
        const fileList = this.createFileList(this.uploadedDocuments.map(doc => doc.file));
        const xhr = this.transactionProfileService.uploadDocumentForAgreement(
            fileList as any,
            this.entityIdDetails.entityId,
            this.agreement.id
        );

        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    let response = false;
                    let documentIds = [];
                    if (xhr.response) {
                        const parsedResponse = JSON.parse(xhr.response);
                        response = parsedResponse.flagResult;
                        documentIds = parsedResponse.arrResult || [];
                        
                        if (documentIds.length > 0) {
                            // Assign document IDs and trigger AI extraction
                            documentIds.forEach((docId, index) => {
                                if (this.uploadedDocuments[index]) {
                                    this.uploadedDocuments[index].documentId = docId;
                                    this.extractDocumentTypeAndTags(index);
                                }
                            });
                        } else if (response) {
                            this.snackBarService.showStatus(
                                parsedResponse,
                                MessageService.ADDED_DOCUMENT_SUCCESS,
                                MessageService.DOCUMENT_FAILURE
                            );
                            this.isUploading = false;
                            this.toggleUploadSection();
                            this.reloadDocuments();
                        } else {
                            this.snackBarService.showStatus(
                                parsedResponse,
                                '',
                                MessageService.DOCUMENT_FAILURE
                            );
                            this.isUploading = false;
                        }
                    }
                } else {
                    this.snackBarService.showStatus(
                        { flagResult: false },
                        '',
                        MessageService.DOCUMENT_FAILURE
                    );
                    this.isUploading = false;
                }
            }
        };
    }

    extractDocumentTypeAndTags(index: number) {
        const uploadedDoc = this.uploadedDocuments[index];
        if (!uploadedDoc.documentId) {
            return;
        }

        uploadedDoc.isAnalyzing = true;
        uploadedDoc.analysisError = undefined;
        this.cdr.detectChanges();

        const url = ConstantsService.DOCUMENT_EXTRACT_TYPE_TAGS + '/' + uploadedDoc.documentId;
        this.requestService.getRequest(url).subscribe(
            response => {
                uploadedDoc.isAnalyzing = false;
                // ResponseVO structure: { flagResult: boolean, arrResult: T }
                if (response && response.flagResult !== false && response.arrResult) {
                    const extracted = response.arrResult;
                    const documentTypeName = extracted.document_type;
                    
                    // Find matching document type
                    if (documentTypeName) {
                        const matchedType = this.documentTypes.find(dt => 
                            dt.name && dt.name.toLowerCase() === documentTypeName.toLowerCase()
                        );
                        
                        if (matchedType) {
                            uploadedDoc.documentType = matchedType.id;
                            uploadedDoc.documentTypeName = matchedType.name;
                            uploadedDoc.formGroup.patchValue({ documentType: matchedType.id });
                            this.onDocumentTypeChange(index);
                        }
                    }
                    
                    // Match tags
                    if (extracted.tags && Array.isArray(extracted.tags)) {
                        const matchedTags = [];
                        extracted.tags.forEach(tagName => {
                            const matchedLabel = this.documentLabels.find(label => 
                                label.name && label.name.toLowerCase() === tagName.toLowerCase()
                            );
                            if (matchedLabel) {
                                matchedTags.push(matchedLabel.value);
                            }
                        });
                        uploadedDoc.tags = matchedTags;
                        uploadedDoc.formGroup.patchValue({ selectedLabels: matchedTags });
                    }
                }
                this.cdr.detectChanges();
            },
            error => {
                uploadedDoc.isAnalyzing = false;
                uploadedDoc.analysisError = 'Failed to extract document type and tags';
                this.cdr.detectChanges();
            }
        );
    }

    onDocumentTypeChange(index: number) {
        const uploadedDoc = this.uploadedDocuments[index];
        const selectedTypeId = uploadedDoc.formGroup.get('documentType').value;
        
        // Use loose equality to handle string/number mismatches
        const selectedDocument = this.documentTypes.find(doc => doc.id == selectedTypeId);
        
        if (selectedDocument) {
            uploadedDoc.documentTypeName = selectedDocument.name;
            // Reset form except document type
            const documentType = uploadedDoc.formGroup.get('documentType').value;
            uploadedDoc.formGroup.reset();
            uploadedDoc.formGroup.patchValue({ documentType: documentType });
            uploadedDoc.formGroup.patchValue({ selectedLabels: uploadedDoc.tags || [] });
        } else if (selectedTypeId != null && selectedTypeId !== '') {
            // If document type is selected but not found in list, still preserve it
            // This handles edge cases where the type might not be in the list yet
            const documentType = uploadedDoc.formGroup.get('documentType').value;
            uploadedDoc.formGroup.reset();
            uploadedDoc.formGroup.patchValue({ documentType: documentType });
            uploadedDoc.formGroup.patchValue({ selectedLabels: uploadedDoc.tags || [] });
        }
        this.cdr.detectChanges();
    }

    confirmAndSaveDocuments() {
        // Validate all documents have required fields
        let allValid = true;
        this.uploadedDocuments.forEach((doc, index) => {
            if (!doc.formGroup.valid) {
                allValid = false;
                // Mark all controls as touched
                Object.keys(doc.formGroup.controls).forEach(key => {
                    doc.formGroup.get(key).markAsTouched();
                });
            }
        });

        if (!allValid) {
            this.snackBarService.showStatus(
                { flagResult: false },
                '',
                'Please fill in all required fields for each document'
            );
            return;
        }

        // Update document data for each uploaded document
        const documentsToUpdate = [];
        this.uploadedDocuments.forEach((uploadedDoc, index) => {
            if (uploadedDoc.documentId) {
                const formValue = uploadedDoc.formGroup.value;
                const document = this.createDocumentForUpdate(uploadedDoc.documentId, formValue, uploadedDoc.documentTypeName);
                documentsToUpdate.push(document);
            }
        });

        if (documentsToUpdate.length > 0) {
            this.entityDocumentsService.addDocument(documentsToUpdate)
                .subscribe(
                    response => {
                        this.snackBarService.showStatus(
                            response,
                            MessageService.ADDED_DOCUMENT_SUCCESS,
                            MessageService.DOCUMENT_FAILURE
                        );
                        this.isUploading = false;
                        this.toggleUploadSection();
                        this.reloadDocuments();
                    },
                    err => {
                        this.snackBarService.showStatus(
                            err,
                            '',
                            MessageService.DOCUMENT_FAILURE
                        );
                        this.isUploading = false;
                    }
                );
        }
    }

    createDocumentForUpdate(documentId: number, formValue: any, documentTypeName: string): Document {
        const document = new Document();
        const existingDoc = this.documents.find(d => d.id === documentId);
        
        // Preserve existing data
        if (existingDoc) {
            document.dateOfUpload = existingDoc.dateOfUpload;
            document.validFrom = existingDoc.validFrom;
            document.validTo = existingDoc.validTo;
            document.docOwner = existingDoc.docOwner || this.loginUser;
        } else {
            document.dateOfUpload = new Date();
            document.docOwner = this.loginUser;
        }
        
        document.entityId = this.entityIdDetails.entityId;
        document.type = formValue.documentType;
        document.id = documentId;
        
        // Update labels
        if (formValue.selectedLabels && formValue.selectedLabels.length > 0) {
            for (const label of formValue.selectedLabels) {
                document.labels.set(label.id, label.name);
            }
        }
        
        // Update metadata based on document type
        if (documentTypeName === 'Passport') {
            if (formValue.passportNumber) document.allMeta.set('passportNumber', String(formValue.passportNumber));
            if (formValue.issuedBy) document.allMeta.set('issuedBy', String(formValue.issuedBy));
            if (formValue.verifiedBy) document.allMeta.set('verifiedBy', String(formValue.verifiedBy));
            if (formValue.validFrom) document.allMeta.set('validFrom', String(formValue.validFrom));
            if (formValue.validTo) document.allMeta.set('validTo', String(formValue.validTo));
            if (formValue.certifiedBy) document.allMeta.set('certifiedBy', String(formValue.certifiedBy));
        } else if (documentTypeName === 'Indemnity Agreement' || 
                   documentTypeName === 'Service Agreement') {
            if (formValue.startDate) document.allMeta.set('startDate', String(formValue.startDate));
            if (formValue.endDate) document.allMeta.set('endDate', String(formValue.endDate));
        } else {
            if (formValue.documentDescription) document.allMeta.set('otherDetails', String(formValue.documentDescription));
        }
        
        return document;
    }

    isUploadDisabled(): boolean {
        return this.uploadedDocuments.length === 0 || this.isUploading;
    }

    isConfirmDisabled(): boolean {
        if (this.uploadedDocuments.length === 0 || this.isUploading) {
            return true;
        }
        // Check if any document is still being analyzed
        return this.uploadedDocuments.some(doc => doc.isAnalyzing);
    }

    createFileList(files: File[]): FileList {
        // Create a FileList-like object that the service can use
        const fileList = {
            length: files.length,
            item: (index: number) => files[index] || null
        } as FileList;
        
        // Add array index access
        files.forEach((file, index) => {
            (fileList as any)[index] = file;
        });
        
        return fileList;
    }

    loadDocuments() {
        if (this.agreement && this.agreement.id) {
            this.transactionProfileService.getDocumentsByAgreementId(this.agreement.id)
                .subscribe(response => {
                    this.documents = response || [];
                    this.documentsLoaded = true;
                    this.zeroAggrement = false;
                    this.updatePagination();
                }, error => {
                    console.error('Error loading documents:', error);
                    this.documents = [];
                    this.documentsLoaded = true;
                    this.updatePagination();
                });
        } else {
            this.documentsLoaded = true;
            this.updatePagination();
        }
    }
    
    updatePagination() {
        this.totalPages = Math.ceil(this.documents.length / this.rowsPerPage);
    }
    
    getPaginatedDocuments(): Document[] {
        if (!this.documents || this.documents.length === 0) {
            return [];
        }
        const start = this.currentPage * this.rowsPerPage;
        const end = start + this.rowsPerPage;
        return this.documents.slice(start, end).filter(doc => doc != null);
    }
    
    onPageChange(page: number) {
        this.currentPage = page;
        // Close all expanded rows when changing pages
        this.expandedRows = {};
    }
    
    onRowsPerPageChange(rows: number) {
        this.rowsPerPage = rows;
        this.currentPage = 0;
        this.updatePagination();
        this.expandedRows = {};
    }
    
    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxVisible = 3;
        
        if (this.totalPages <= maxVisible) {
            for (let i = 0; i < this.totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (this.currentPage < 2) {
                pages.push(0, 1, 2, -1, this.totalPages - 1);
            } else if (this.currentPage > this.totalPages - 3) {
                pages.push(0, -1, this.totalPages - 3, this.totalPages - 2, this.totalPages - 1);
            } else {
                pages.push(0, -1, this.currentPage - 1, this.currentPage, this.currentPage + 1, -1, this.totalPages - 1);
            }
        }
        
        return pages;
    }
    
    Math = Math; // Expose Math to template

    downloadDocument(id: number) {
        window.open(ConstantsService.APP_BE_URL + '/' + ConstantsService.DOWNLOAD_DOCUMENT + '?documentId=' + id);
    }

    removeDocument(document: Document): void {
        const transProfileDocumentModel = new Document();
        transProfileDocumentModel.id = document.id;
        const agreementId = this.agreement.id;
        this.transactionProfileService.removeDocument(transProfileDocumentModel, agreementId)
            .subscribe(response => {
                if (response.flagResult) {
                    this.documents.splice(this.documents.indexOf(document), 1);
                }
                this.trackingService
                    .emitEvent(
                    TrackingConstants.TRANS_PROFILE_DOCUMENT,
                    TrackingConstants.DELETE_DOCUMENT,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarServiceForDocumentRemoval(response);
            }, err => {
                this.trackingService
                    .emitEvent(
                    TrackingConstants.TRANS_PROFILE_DOCUMENT,
                    TrackingConstants.DELETE_DOCUMENT,
                    TrackingConstants.FAILURE, 0);
                this.callSnackBarServiceForDocumentRemoval(err);
            }
            );
    }

    reloadDocuments() {
        this.transactionProfileService.getDocumentsByAgreementId(this.agreement.id).subscribe(
            response => {
                this.documents = response || [];
            }
        );
    }

    callSnackBarServiceForDocumentRemoval(response: any) {
        this.snackBarService
            .showStatus(response,
            MessageService.DOCUMENT_DELETE_SUCCESS,
            MessageService.DOCUMENT_DELETE_FAILURE);
    }

    close() {
        this.dialog.close(true);
    }

    getSelectedDocumentTypeName(index: number): string {
        const uploadedDoc = this.uploadedDocuments[index];
        const selectedTypeId = uploadedDoc.formGroup.get('documentType').value;
        const selectedDocument = this.documentTypes.find(doc => doc.id === selectedTypeId);
        return selectedDocument ? selectedDocument.name : '';
    }

    hasUploadedDocumentsWithIds(): boolean {
        return this.uploadedDocuments.some(doc => !!doc.documentId);
    }

    // Tag management methods
    getFilteredTags(index: number): any[] {
        const searchText = (this.tagSearchTexts[index] || '').toLowerCase();
        const uploadedDoc = this.uploadedDocuments[index];
        const selectedTagIds = (uploadedDoc.formGroup.get('selectedLabels').value || []).map(tag => tag.id);
        
        return this.documentLabels.filter(label => {
            const matchesSearch = !searchText || label.name.toLowerCase().includes(searchText);
            const notSelected = !selectedTagIds.includes(label.value.id);
            return matchesSearch && notSelected;
        });
    }

    onTagSearch(index: number, searchText: string) {
        this.tagSearchTexts[index] = searchText;
        this.showTagDropdowns[index] = true;
        this.cdr.detectChanges();
    }

    onTagInputFocus(index: number) {
        this.showTagDropdowns[index] = true;
        this.cdr.detectChanges();
    }

    onTagInputBlur(index: number) {
        // Delay hiding dropdown to allow click events
        setTimeout(() => {
            this.showTagDropdowns[index] = false;
            this.cdr.detectChanges();
        }, 200);
    }

    onTagContainerClick(index: number, event: MouseEvent) {
        // If clicking directly on the container (not on chips or input), focus the input
        const target = event.target as HTMLElement;
        if (target.classList.contains('tag-chips-container')) {
            const input = target.querySelector('.tag-search-input') as HTMLInputElement;
            if (input && !input.disabled) {
                input.focus();
            }
        }
    }

    addTag(index: number, tag: any) {
        const uploadedDoc = this.uploadedDocuments[index];
        const currentTags = uploadedDoc.formGroup.get('selectedLabels').value || [];
        
        // Check if tag is already selected
        if (!currentTags.find(t => t.id === tag.value.id)) {
            currentTags.push(tag.value);
            uploadedDoc.formGroup.patchValue({ selectedLabels: currentTags });
            uploadedDoc.tags = currentTags;
        }
        
        // Clear search and hide dropdown
        this.tagSearchTexts[index] = '';
        this.showTagDropdowns[index] = false;
        this.cdr.detectChanges();
    }

    removeTag(index: number, tagId: number) {
        const uploadedDoc = this.uploadedDocuments[index];
        const currentTags = uploadedDoc.formGroup.get('selectedLabels').value || [];
        const filteredTags = currentTags.filter(t => t.id !== tagId);
        
        uploadedDoc.formGroup.patchValue({ selectedLabels: filteredTags });
        uploadedDoc.tags = filteredTags;
        this.cdr.detectChanges();
    }

    getSelectedTags(index: number): Array<{ id: number; name: string }> {
        const uploadedDoc = this.uploadedDocuments[index];
        return uploadedDoc.formGroup.get('selectedLabels').value || [];
    }

    trackByDocumentTypeId(index: number, documentType: ApplicationValue): any {
        return documentType ? documentType.id : index;
    }

    // Metadata display methods
    toggleRowExpansion(documentId: number) {
        this.expandedRows[documentId] = !this.expandedRows[documentId];
    }

    isRowExpanded(documentId: number): boolean {
        return !!this.expandedRows[documentId];
    }

    getDocumentTypeName(document: Document): string {
        if (!document || !document.type) {
            return 'Unknown';
        }
        const docType = this.documentTypes.find(dt => dt.id === document.type);
        return docType ? docType.name : 'Unknown';
    }

    getDocumentTags(document: Document): Array<{ id: number; name: string }> {
        if (!document || !document.labels) {
            return [];
        }
        const tags: Array<{ id: number; name: string }> = [];
        const labels: any = document.labels;
        
        // Handle Map type
        if (labels instanceof Map) {
            labels.forEach((name: string, id: number) => {
                tags.push({ id, name });
            });
        }
        // Handle array type
        else if (Array.isArray(labels)) {
            (labels as any[]).forEach((label: any) => {
                if (label && (label.id || label.value)) {
                    const id = label.id || (label.value && label.value.id);
                    const name = label.name || (label.value && label.value.name) || label.label;
                    if (id && name) {
                        tags.push({ id, name });
                    }
                }
            });
        }
        // Handle plain object type
        else if (typeof labels === 'object' && labels !== null) {
            const labelsObj = labels as { [key: string]: any };
            Object.keys(labelsObj).forEach((key) => {
                const id = parseInt(key, 10);
                const name = labelsObj[key];
                if (!isNaN(id) && name) {
                    tags.push({ id, name });
                }
            });
        }
        
        return tags;
    }

    getFormattedMetadata(document: Document): Array<{ key: string; value: string; label: string }> {
        if (!document || !document.allMeta) {
            return [];
        }
        
        const metadata: Array<{ key: string; value: string; label: string }> = [];
        const metadataLabels: { [key: string]: string } = {
            'passportNumber': 'Passport Number',
            'issuedBy': 'Issued By',
            'verifiedBy': 'Verified By',
            'validFrom': 'Valid From',
            'validTo': 'Valid To',
            'certifiedBy': 'Certified By',
            'startDate': 'Start Date',
            'endDate': 'End Date',
            'otherDetails': 'Description'
        };

        const allMeta = document.allMeta;

        // Handle Map type
        if (allMeta instanceof Map) {
            if (allMeta.size === 0) {
                return [];
            }
            allMeta.forEach((value: string, key: string) => {
                if (value) {
                    metadata.push({
                        key: key,
                        value: value,
                        label: metadataLabels[key] || this.formatMetadataKey(key)
                    });
                }
            });
        }
        // Handle plain object type
        else if (typeof allMeta === 'object' && allMeta !== null) {
            const allMetaObj = allMeta as any;
            const keys = Object.keys(allMetaObj);
            if (keys.length === 0) {
                return [];
            }
            keys.forEach((key) => {
                const value = allMetaObj[key];
                if (value) {
                    metadata.push({
                        key: key,
                        value: String(value),
                        label: metadataLabels[key] || this.formatMetadataKey(key)
                    });
                }
            });
        }

        return metadata;
    }

    formatMetadataKey(key: string): string {
        // Convert camelCase or snake_case to Title Case
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }

    hasMetadata(document: Document): boolean {
        if (!document || !document.allMeta) {
            return false;
        }
        
        // Handle Map type
        if (document.allMeta instanceof Map) {
            return document.allMeta.size > 0;
        }
        // Handle plain object type
        else if (typeof document.allMeta === 'object') {
            return Object.keys(document.allMeta).length > 0;
        }
        
        return false;
    }
    
    extractAndUpdateDocumentMetadata(documentId: number, fileName: string) {
        const url = ConstantsService.DOCUMENT_EXTRACT_TYPE_TAGS + '/' + documentId;
        this.requestService.getRequest(url).subscribe(
            response => {
                if (response && response.flagResult !== false && response.arrResult) {
                    const extracted = response.arrResult;
                    const documentTypeName = extracted.document_type;
                    const tags = extracted.tags || [];
                    const description = extracted.description || fileName;
                    
                    // Find matching document type
                    let matchedTypeId = null;
                    if (documentTypeName) {
                        const matchedType = this.documentTypes.find(dt => 
                            dt.name && dt.name.toLowerCase() === documentTypeName.toLowerCase()
                        );
                        if (matchedType) {
                            matchedTypeId = matchedType.id;
                        }
                    }
                    
                    // Find matching tags
                    const matchedTags = [];
                    if (tags && Array.isArray(tags)) {
                        tags.forEach((tagName: string) => {
                            const matchedLabel = this.documentLabels.find(label => 
                                label.name && label.name.toLowerCase() === tagName.toLowerCase()
                            );
                            if (matchedLabel) {
                                matchedTags.push(matchedLabel.value);
                            }
                        });
                    }
                    
                    // Create updated document
                    const document = new Document();
                    document.id = documentId;
                    document.entityId = this.entityIdDetails.entityId;
                    document.type = matchedTypeId;
                    document.docOwner = this.loginUser;
                    document.dateOfUpload = new Date();
                    
                    // Set labels
                    if (matchedTags.length > 0) {
                        for (const tag of matchedTags) {
                            document.labels.set(tag.id, tag.name);
                        }
                    }
                    
                    // Set metadata based on document type
                    let docTypeName = '';
                    if (matchedTypeId) {
                        const matchedDocType = this.documentTypes.find(dt => dt.id === matchedTypeId);
                        docTypeName = matchedDocType ? matchedDocType.name : '';
                    }
                    
                    if (docTypeName === 'Passport') {
                        // Passport metadata will be empty initially
                    } else if (docTypeName === 'Indemnity Agreement' || docTypeName === 'Service Agreement') {
                        // Agreement dates will be empty initially
                    } else {
                        // Set description for other types
                        if (description) {
                            document.allMeta.set('otherDetails', description);
                        }
                    }
                    
                    // Update the document
                    this.entityDocumentsService.addDocument([document])
                        .subscribe(
                            updateResponse => {
                                // Silently update - don't show notification to avoid spam
                                this.reloadDocuments();
                            },
                            err => {
                                // Silently fail - document is already uploaded
                                console.error('Failed to update document with extracted metadata:', err);
                            }
                        );
                }
            },
            error => {
                // Silently fail - document is already uploaded
                console.error('Failed to extract document metadata:', error);
            }
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/transaction-profile/agreements/agreement-documents-dialog.component.ts